/**
 * LAS NOTIFICACIONES AL TELÉFONO
 * =============================================================================
 * El botón que aparece en la página para recibir avisos.
 *
 * -----------------------------------------------------------------------------
 * CÓMO FUNCIONA, Y POR QUÉ PIDE PERMISO
 * -----------------------------------------------------------------------------
 *   1 · Se comprueba que el navegador sepa hacerlo. Los de escritorio viejos y
 *        algunos navegadores no pueden.
 *   2 · Se registra el «trabajador» (`sw.js`), que es el archivo que recibe los
 *        avisos con la página cerrada.
 *   3 · El navegador pide permiso a la persona. Eso NO se puede saltar: si no
 *        acepta, no hay avisos. Y solo se puede preguntar una vez.
 *   4 · Se pide al servicio de avisos una dirección propia para este teléfono, y
 *        se guarda en el panel junto con sus claves.
 *
 * -----------------------------------------------------------------------------
 * SI YA DIJO QUE NO
 * -----------------------------------------------------------------------------
 * El navegador no deja volver a preguntar. En ese caso, en lugar del botón se
 * explica cómo activarlas desde los ajustes del navegador.
 */

import { useCallback, useEffect, useState } from 'react';
import { CONFIG } from '../config';
import { Icono } from './Iconos';
import { useIdioma } from '../i18n/useIdioma';
import { useAjustes } from '../i18n/useAjustes';

/** La clave pública del servidor, que se pide al cargar. */
let clavePublica: string | null = null;

/** Convierte la clave del servidor al formato que pide el navegador. */
function aBytes(base64: string) {
  const relleno = '='.repeat((4 - (base64.length % 4)) % 4);
  const s = (base64 + relleno).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(s);
  const salida = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) salida[i] = bin.charCodeAt(i);
  return salida;
}

type Estado = 'cargando' | 'puede' | 'activadas' | 'bloqueadas' | 'no-se-puede';

export function Notificaciones() {
  const { textos } = useIdioma();
  const { valor, activo } = useAjustes();
  const t = textos.notificaciones;
  const [estado, setEstado] = useState<Estado>('cargando');
  const [ocupado, setOcupado] = useState(false);

  // --- Se mira en qué situación está este navegador ---
  useEffect(() => {
    const revisar = async () => {
      // ¿El navegador sabe hacerlo?
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
        setEstado('no-se-puede');
        return;
      }

      if (Notification.permission === 'granted') {
        // Puede que ya esté suscrito.
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        setEstado(sub ? 'activadas' : 'puede');
        return;
      }

      // Si ya dijo que no, el navegador no deja volver a preguntar.
      setEstado(Notification.permission === 'denied' ? 'bloqueadas' : 'puede');
    };

    revisar().catch(() => setEstado('no-se-puede'));
  }, []);

  // --- Se pide la clave pública al servidor ---
  useEffect(() => {
    if (!activo('avisos_activos')) return;
    fetch(CONFIG.notificaciones.clave)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.publica) clavePublica = d.publica; })
      .catch(() => {});
  }, []);

  const activar = useCallback(async () => {
    setOcupado(true);
    try {
      // 1 · El permiso. Si no lo da, no se sigue.
      const permiso = await Notification.requestPermission();
      if (permiso !== 'granted') {
        setEstado(permiso === 'denied' ? 'bloqueadas' : 'puede');
        return;
      }

      // 2 · El trabajador que recibe los avisos.
      const reg = await navigator.serviceWorker.register(CONFIG.notificaciones.worker);

      // 3 · La suscripción con el servicio de avisos del navegador.
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: clavePublica ? aBytes(clavePublica) : undefined,
      });

      // 4 · Y se guarda en el panel.
      const d = sub.toJSON();
      await fetch(CONFIG.notificaciones.suscribir, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: d.endpoint,
          p256dh: d.keys?.p256dh,
          auth: d.keys?.auth,
          etiqueta: navigator.platform || 'Teléfono',
        }),
      });

      setEstado('activadas');
    } catch {
      setEstado('puede');
    } finally {
      setOcupado(false);
    }
  }, []);

  // Si la función está apagada desde el panel, no se enseña nada.
  if (!activo('avisos_activos')) return null;
  if (estado === 'cargando' || estado === 'no-se-puede') return null;

  return (
    <section className="seccion seccion--avisos">
      <div className="caja avisos">
        <span className="aro" style={{ width: 56, height: 56 }}>
          <Icono nombre="campana" tam={24} />
        </span>

        <div className="avisos__texto">
          <h2 className="avisos__titulo">{valor('avisos_titulo', t.titulo)}</h2>
          <p className="avisos__bajada">
            {estado === 'activadas'
              ? t.activadas
              : estado === 'bloqueadas'
                ? t.bloqueadas
                : valor('avisos_invitacion', t.invitacion)}
          </p>
        </div>

        {estado === 'puede' && (
          <button type="button" className="boton boton--oro" onClick={activar} disabled={ocupado}>
            <span>{ocupado ? t.activando : t.boton}</span>
            <Icono nombre="campana" tam={17} />
          </button>
        )}

        {estado === 'activadas' && (
          <span className="avisos__listo">
            <Icono nombre="palomita" tam={18} /> {t.listo}
          </span>
        )}
      </div>
    </section>
  );
}
